/**
 * InviteUserModal — thin wrapper around @insio/ui-toolkit module.
 * Integrates GraphQL mutation, resolves role translations and adds the org unit pick.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Button, InputWrapper, InviteUserModal as InviteUserModalBase } from '@insio/ui-toolkit';
import { GraphQLErrorCode, isValidEmail } from '@insio/validation';
import { useTranslation } from 'react-i18next';
import { CombinedGraphQLErrors } from '@apollo/client';
import { InviteOrgUnitsDocument, InviteUserDocument, type UserRole } from '@/graphql';
import { useRoleOptions } from '@/features/admin/hooks/use-role-options';
import { buildOrgTree } from '@/features/admin/components/code-lists/org-structure/use-org-structure';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToday } from '@/hooks/use-today';
import { OrgUnitPickerModal, type OrgUnitPick } from './org-unit-picker-modal/OrgUnitPickerModal';

/** Id of the org unit field; its hint is `${ORG_UNIT_FIELD_ID}-hint`. */
const ORG_UNIT_FIELD_ID = 'invite-org-unit-field';

interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteUserModal({ onClose, onSuccess }: InviteUserModalProps) {
  const { options: roleOptions } = useRoleOptions();
  const { t } = useTranslation('ui');
  const tRoleDescription = useEnumTranslation('userRoleDescription');
  const tUsers = useFeatureTranslation('systemSettings.users');
  const [inviteUser, { loading, error }] = useMutation(InviteUserDocument);

  // Only units in effect today are on offer; the field stays hidden while there are none.
  const today = useToday();
  const { data: orgUnitsData } = useQuery(InviteOrgUnitsDocument, {
    variables: { effectiveOn: today },
    fetchPolicy: 'cache-and-network'
  });
  const units = useMemo(() => orgUnitsData?.orgUnits ?? [], [orgUnitsData?.orgUnits]);
  const orgTree = useMemo(() => buildOrgTree(units), [units]);
  const [orgUnitPick, setOrgUnitPick] = useState<OrgUnitPick | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // A pick whose unit is no longer on offer counts as no pick.
  const pickedUnit = orgUnitPick
    ? (units.find(unit => unit.id === orgUnitPick.unitId) ?? null)
    : null;
  const activePick = pickedUnit ? orgUnitPick : null;
  const replacedHead = activePick?.asManager ? (pickedUnit?.manager?.displayName ?? null) : null;
  const headHint = replacedHead
    ? tUsers('orgUnit.replacesHead', { name: replacedHead })
    : undefined;

  // One trigger for choosing and changing: it stays mounted, so focus can return to it.
  const triggerRef = useRef<HTMLButtonElement>(null);

  const mapErrorByCode = (code: string | undefined): string | undefined => {
    switch (code) {
      case GraphQLErrorCode.EMAIL_CONFLICT_OTHER_ORG:
        return t('modules.modals.inviteUser.errors.emailAlreadyRegistered');
      case GraphQLErrorCode.EMAIL_CONFLICT_SAME_ORG:
        return t('modules.modals.inviteUser.errors.emailAlreadyInOrg');
      case GraphQLErrorCode.INVITATION_ALREADY_SENT:
        return t('modules.modals.inviteUser.errors.invitationAlreadySent');
      default:
        return undefined;
    }
  };

  const handleSubmit = async (data: { email: string; roles: string[] }) => {
    const result = await inviteUser({
      variables: {
        input: {
          email: data.email.trim(),
          roles: data.roles as UserRole[],
          ...(activePick && {
            orgUnitId: activePick.unitId,
            asOrgUnitManager: activePick.asManager
          })
        }
      }
    });

    if (result.data?.inviteUser.success) {
      onSuccess();
    } else if (result.data?.inviteUser) {
      // Resolver returned success:false without throwing a GraphQL exception
      throw new Error(
        result.data.inviteUser.message ?? t('modules.modals.inviteUser.errors.inviteError')
      );
    }
  };

  const apolloErrorCode = CombinedGraphQLErrors.is(error)
    ? (error.errors[0]?.extensions?.['code'] as string | undefined)
    : undefined;

  const errorMessage = mapErrorByCode(apolloErrorCode) ?? error?.message;

  const orgUnitField =
    units.length > 0 ? (
      <InputWrapper
        label={tUsers('orgUnit.label')}
        labelSize='sm'
        htmlFor={ORG_UNIT_FIELD_ID}
        hint={headHint}
      >
        {/* A named group, not a labelled control: each button keeps its own name. */}
        <div role='group' aria-label={tUsers('orgUnit.label')} className='flex items-center gap-sm'>
          {pickedUnit && activePick && (
            <span
              className='min-w-0 flex-1 truncate text-sm text-foreground-base'
              data-testid='invite-org-unit'
            >
              {`${pickedUnit.code} — ${pickedUnit.name}`}
              {activePick.asManager && (
                <span className='text-foreground-muted'>{` · ${tUsers('orgUnit.asManagerTag')}`}</span>
              )}
            </span>
          )}
          <Button
            ref={triggerRef}
            variant='default'
            size='sm'
            icon={activePick ? undefined : 'plus'}
            onClick={() => setPickerOpen(true)}
            disabled={loading}
            aria-describedby={headHint ? `${ORG_UNIT_FIELD_ID}-hint` : undefined}
          >
            {activePick ? tUsers('orgUnit.change') : tUsers('orgUnit.choose')}
          </Button>
          {activePick && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                setOrgUnitPick(null);
                triggerRef.current?.focus();
              }}
              disabled={loading}
            >
              {tUsers('orgUnit.remove')}
            </Button>
          )}
        </div>
      </InputWrapper>
    ) : undefined;

  return (
    <>
      <InviteUserModalBase
        open={true}
        roleOptions={roleOptions}
        noRolesHint={t('modules.modals.inviteUser.noRolesHint', {
          description: tRoleDescription('MEMBER')
        })}
        onSubmit={handleSubmit}
        onClose={onClose}
        loading={loading}
        error={errorMessage}
        validateEmail={isValidEmail}
        extraFields={orgUnitField}
      />
      {/* Outside the invite form, so the picker's own submit never reaches it. */}
      {pickerOpen && (
        <OrgUnitPickerModal
          tree={orgTree}
          value={activePick}
          onConfirm={pick => {
            setOrgUnitPick(pick);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}
